import React, { useMemo, useState } from 'react';
import { Col, Divider, Row, Select, Typography } from 'antd';
import moment from 'moment/moment';
import {
  Currency,
  FilteredTable,
  TextSearchTableFilter,
} from '@common/components';
import { FontSize } from '@common/tokens';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import {
  BuilderConfig,
  filterableColumnBuilder,
} from 'works/legacy/lib/table-filters-helper';
import { ToggleButton } from 'works/legacy/components/billing-rates-drawer/_toggle-button';
import {
  applyFilterIfKeyIsValid,
  calculateBillingRate,
  ColumnData,
  filterByName,
  filterByPayCodes,
  Filters,
  formatFactor,
  parsePayCode,
} from 'works/legacy/components/billing-rates-drawer/_billing-rates-drawer-helpers';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';
import { WorksRateCardType } from '@pay-bill/graphql/queries';

const { Text } = Typography;

const LOCALSTORAGE_FILTER_KEY = 'assignment-term-billing-rates-filter';

function getColumns(
  billingRegularRate: number,
  builderConfig: BuilderConfig<ColumnData>
) {
  const column = filterableColumnBuilder(builderConfig);

  return [
    column({
      title: 'Billing Group',
      key: 'name',
      width: '50%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
    }),
    column({
      title: 'Pay Codes',
      key: 'payCode',
      width: '50%',
      sorter: false,
      filterDropdown: TextSearchTableFilter,
      render: (value, record) => {
        return record?.children ? null : value;
      },
    }),
    column({
      title: 'Expression',
      key: 'expression',
      width: '25%',
      sorter: false,
      render: (value) => {
        if (!value) return '';

        return <div style={{ textTransform: 'capitalize' }}>{value}</div>;
      },
    }),
    column({
      title: 'Factor',
      key: 'factor',
      width: '30%',
      sorter: false,
      render: (value, record) => {
        if (!value) return '';

        return formatFactor(value, record.expression);
      },
    }),
    column({
      title: 'Billing Rate',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Antd just complaining since this is a custom computed column
      key: 'billingRate',
      width: '30%',
      sorter: false,
      render: (_value, record) => {
        if (!record) return '';

        return calculateBillingRate(
          record.expression,
          record.factor,
          billingRegularRate
        );
      },
    }),
  ];
}

export const BillingRatesDrawerBody = ({
  assignmentTerm,
  worksRateCards,
}: {
  assignmentTerm: {
    id: string;
    effectiveAt: string;
    billingRegularRate: number;
  };
  worksRateCards?: WorksRateCardType[] | null;
}) => {
  const [rateCard, setRateCard] = useState<
    WorksRateCardType | undefined | null
  >(worksRateCards?.[0]);
  const [expandedRowKeys, setExpandedRowKeys] = useState(['']);

  const [selectedFilters, setSelectedFilters] = useStoredFilters(
    LOCALSTORAGE_FILTER_KEY,
    {
      store: localStorageStore,
      defaults: {} as Filters,
    }
  );

  const columns = getColumns(assignmentTerm.billingRegularRate, {
    paginated: false,
    filterConfig: {
      selectedFilters,
    },
  });

  function onExpand(expanded: boolean, key: string) {
    if (expanded) {
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  }

  const hasFilters = useHasFilters(selectedFilters);

  const viewData: ColumnData[] = useMemo(() => {
    if (!rateCard?.worksRateCardItems) {
      return [];
    }

    const tableData = rateCard?.worksRateCardItems.map((ob) => ({
      id: ob.id,
      name: ob.name,
      expression: ob.expression,
      factor: ob.factor,
      payCode: ob.payCode,
      children: parsePayCode(ob.payCode),
    }));

    if (!hasFilters) {
      return tableData;
    }

    const partiallyFilteredTableData = applyFilterIfKeyIsValid(
      tableData,
      selectedFilters.payCode,
      filterByPayCodes
    );

    return applyFilterIfKeyIsValid(
      partiallyFilteredTableData,
      selectedFilters.name,
      filterByName
    );
  }, [rateCard?.worksRateCardItems, selectedFilters, hasFilters]);

  return (
    <div className="billing-rate-drawer-body">
      <Text strong className="billing-rates-drawer-title">
        Assignment
      </Text>
      <Row gutter={[0, 16]} align="middle">
        <Col span={6}>
          <Text type="secondary">Effective Term</Text>
        </Col>
        <Col span={16} className="billing-rates-drawer-content">
          <span>{`${moment(assignmentTerm.effectiveAt).format(
            'L, ddd'
          )}`}</span>
        </Col>
      </Row>
      <Row gutter={[0, 16]} align="middle">
        <Col span={6}>
          <Text type="secondary">Regular Base Rate</Text>
        </Col>
        <Col span={16} className="billing-rates-drawer-content">
          <span>
            <Currency value={assignmentTerm.billingRegularRate} />
          </span>
        </Col>
      </Row>
      <Divider />
      <Text strong className="billing-rates-drawer-title">
        Billing Rules
      </Text>
      <Row gutter={[0, 16]} align="middle">
        <Col span={5}>
          <span>Effective at</span>
        </Col>
        <Col span={16}>
          <Select
            className="select-width billing-rate-select"
            size="large"
            optionFilterProp="children"
            value={rateCard?.id}
            onSelect={(value) => {
              const rateCard = worksRateCards?.find(
                (card) => card.id === value
              );
              setRateCard(rateCard || worksRateCards?.[0]);
            }}
          >
            {worksRateCards?.map((rateCard, id) => (
              <Select.Option
                key={id}
                value={rateCard.id}
                title={rateCard.effectiveDate}
              >
                <span>{moment(rateCard.effectiveDate).format('LL')}</span>
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={[0, 16]} align="middle">
        <div className="table-wrapper">
          <FilteredTable
            rowKey="id"
            className="billing-rates-table"
            width={220}
            scroll={{ x: '100%' }}
            filters={selectedFilters}
            onChange={() => {}}
            onFilterChange={setSelectedFilters}
            localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
            dataSource={viewData}
            columns={columns}
            loading={!rateCard}
            pagination={{
              hideOnSinglePage: true,
              pageSize: viewData.length,
              total: viewData.length,
            }}
            expandable={{
              expandRowByClick: false,
              expandedRowKeys,
              indentSize: 0,
              defaultExpandAllRows: true,
              expandIcon: ({
                expanded,
                record,
              }: {
                expanded: boolean;
                record: ColumnData;
              }) => {
                if (!record?.children?.every((child) => child?.payCode))
                  return <span style={{ marginLeft: 26 }} />;

                return (
                  <ToggleButton
                    expanded={expanded}
                    onClick={() => onExpand(expanded, record.id)}
                  />
                );
              },
            }}
            onRow={(record: ColumnData) => ({
              onClick: record.name
                ? () => onExpand(expandedRowKeys.includes(record.id), record.id)
                : null,
            })}
          />
        </div>
      </Row>
      <style jsx>
        {`
          :global(.billing-rates-drawer-title) {
            font-size: ${FontSize.md};
            margin-top: 24px;
            margin-bottom: 6px;
          }
          :global(.billing-rates-table) {
            width: 710px;
          }
          :global(.billing-rates-table .ant-table-row) {
            height: 49px;
          }
          :global(.billing-rates-drawer-content) {
            margin-top: 10px;
          }
          :global(.billing-rate-select) {
            width: 200px;
          }
        `}
      </style>
    </div>
  );
};
